import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import textFileServiceInstance from '../../services/TextFileService';
import { createTextFileDialogHelpItems } from '../../utils/helpItems';
import HelpOverlay from '../common/HelpOverlay';
import { SelectLanguageComponent, flatLangOptions } from '../subtitles/SelectLanguageComponent';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { startTrackingForNewContent } from '../tasks/startTracking';
import TaskFieldsSection, { emptyTaskFields } from '../tasks/TaskFieldsSection';
import { targetsForWaiting } from '../tasks/taskUi';

/**
 * waiting: set when this opens from a waiting task, as { title, languages, folderField }. It starts
 * from the task and has no Bulk Upload or Track this work, because the file goes to that task.
 */
const CreateTextFileDialog = ({
    open,
    onClose,
    currentPath,
    onFileCreated,
    darkMode = false,
    themeVariant = 'vibrant',
    waiting = null,
}) => {
    useTrackUsage('CreateTextFileDialog', 'dialog', 'textfiles', open);
    const [activeTab, setActiveTab] = useState(0);
    const [fileName, setFileName] = useState(waiting?.title || '');
    const [fileContent, setFileContent] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sourceLanguage, setSourceLanguage] = useState('Gujarati');
    
    const [autoTranslate, setAutoTranslate] = useState(false);

    // Bulk upload state
    const [bulkFiles, setBulkFiles] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkError, setBulkError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState({});
    const [uploadResults, setUploadResults] = useState([]);
    const [isBulkDragOver, setIsBulkDragOver] = useState(false);
    const [isSingleDragOver, setIsSingleDragOver] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    
    // Initialize target languages: English and Hindi by default
    const getDefaultTargetLanguages = () => {
        return flatLangOptions.filter(
            (lang) => lang.label === 'English' || lang.label === 'Hindi'
        );
    };
    const [targetLanguages, setTargetLanguages] = useState(() => {
        const fromTask = targetsForWaiting(waiting?.languages, sourceLanguage, flatLangOptions);
        return fromTask.length > 0 ? fromTask : getDefaultTargetLanguages();
    });
    const [taskFields, setTaskFields] = useState(emptyTaskFields());

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setError(null);
        setBulkError(null);
        // Clear content and uploaded file when switching tabs to avoid confusion
        setFileContent('');
        setUploadedFile(null);
    };

    // A waiting task keeps its title
    const nameFromFile = (name) => {
        if (!waiting) setFileName(name);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setUploadedFile(file);
            nameFromFile(file.name);

            // Read file content
            const reader = new FileReader();
            reader.onload = (e) => {
                setFileContent(e.target.result);
            };
            reader.readAsText(file);
        }
    };

    const handleBulkFileUpload = (event) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setBulkFiles(fileArray);
            setUploadResults([]);
            setBulkError(null);
        }
    };

    const handleBulkDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsBulkDragOver(false);
        if (bulkLoading) return;
        const files = Array.from(event.dataTransfer.files).filter((f) =>
            /\.(txt|text|html|htm)$/i.test(f.name)
        );
        if (files.length > 0) {
            setBulkFiles(files);
            setUploadResults([]);
            setBulkError(null);
        }
    };

    const handleSingleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsSingleDragOver(false);
        if (isLoading) return;
        const file = Array.from(event.dataTransfer.files).find((f) =>
            /\.(txt|text|html|htm)$/i.test(f.name)
        );
        if (file) {
            setUploadedFile(file);
            nameFromFile(file.name);
            const reader = new FileReader();
            reader.onload = (e) => setFileContent(e.target.result);
            reader.readAsText(file);
        }
    };

    const handleRemoveBulkFile = (index) => {
        setBulkFiles(bulkFiles.filter((_, i) => i !== index));
    };

    const handleSourceLanguageChange = (event) => {
        const newSourceLanguage = event.target.value;
        setSourceLanguage(newSourceLanguage);

        // Remove the new source language from target languages if it's already selected
        setTargetLanguages((prev) => {
            let filtered = prev.filter((lang) => lang.label !== newSourceLanguage);

            // If all targets were removed, auto-select first non-source language
            if (filtered.length === 0) {
                const firstAvailable = flatLangOptions.find(
                    (lang) => lang.label !== newSourceLanguage
                );
                if (firstAvailable) {
                    filtered = [firstAvailable];
                }
            }

            return filtered;
        });
    };

    const handleTargetLanguagesChange = (selectedLanguages) => {
        // selectedLanguages is an array of option objects from SelectLanguageComponent
        // Filter out the source language from target languages
        const filteredLanguages = selectedLanguages.filter((lang) => lang.label !== sourceLanguage);
        setTargetLanguages(filteredLanguages);
    };

    const validateForm = () => {
        if (!fileName.trim()) {
            setError('File name is required');
            return false;
        }

        if (activeTab === 0 && !fileContent.trim()) {
            setError('Text content is required for creating a new file');
            return false;
        }

        if (targetLanguages.length === 0) {
            setError('At least one target language must be selected');
            return false;
        }

        // Add extension if not present
        if (!fileName.includes('.')) {
            setFileName(fileName + '.txt');
        }

        return true;
    };

    const validateBulkForm = () => {
        if (bulkFiles.length === 0) {
            setBulkError('Please select at least one file');
            return false;
        }

        if (targetLanguages.length === 0) {
            setBulkError('At least one target language must be selected');
            return false;
        }

        return true;
    };

    const handleCreate = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        setError(null);

        try {
            // Pass the language information along with the file
            const result = await textFileServiceInstance.createTextFile(
                currentPath,
                fileName,
                fileContent,
                sourceLanguage,
                targetLanguages,
                autoTranslate,
            );

            onFileCreated(result);
            startTrackingForNewContent('TEXT', [result?.id], taskFields);
            handleClose();
        } catch (err) {
            console.error('Error creating text file:', err);
            setError(err.response?.data?.error || 'Failed to create text file');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkCreate = async () => {
        if (!validateBulkForm()) return;

        setBulkLoading(true);
        setBulkError(null);
        setUploadResults([]);

        try {
            const results = await textFileServiceInstance.createTextFilesBulk(
                currentPath,
                bulkFiles,
                sourceLanguage,
                targetLanguages,
                autoTranslate,
                (fileIndex, progress) => {
                    setUploadProgress((prev) => ({
                        ...prev,
                        [fileIndex]: progress,
                    }));
                }
            );

            setUploadResults(results);
            
            // Notify parent of successful uploads
            const successfulFiles = results.filter(r => r.status === 'success');
            if (successfulFiles.length > 0) {
                successfulFiles.forEach(r => onFileCreated(r.result));
                startTrackingForNewContent('TEXT', successfulFiles.map((r) => r.result?.id), taskFields);
            }

            // Auto close if all successful
            if (results.every(r => r.status === 'success')) {
                setTimeout(() => handleClose(), 2000);
            }
        } catch (err) {
            console.error('Error during bulk upload:', err);
            setBulkError(err.message || 'Failed to upload files');
        } finally {
            setBulkLoading(false);
        }
    };

    const handleClose = () => {
        setFileName('');
        setFileContent('');
        setUploadedFile(null);
        setError(null);
        setBulkError(null);
        setActiveTab(0);
        setSourceLanguage('Gujarati');
        setTargetLanguages(getDefaultTargetLanguages());
        setAutoTranslate(false);
        setBulkFiles([]);
        setUploadProgress({});
        setUploadResults([]);
        setTaskFields(emptyTaskFields());
        onClose();
    };

    // Get language options for source language dropdown (single select)
    const sourceLanguageOptions = flatLangOptions.map((lang) => lang.label);

    // Get available target languages (exclude source language)
    const availableTargetLanguages = flatLangOptions
        .map((lang) => lang.label)
        .filter((lang) => lang !== sourceLanguage);

    const getStatusColor = (status) => {
        switch (status) {
            case 'success':
                return 'success';
            case 'error':
                return 'error';
            case 'pending':
                return 'info';
            default:
                return 'default';
        }
    };

    return (
        <>
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                style: {
                    backgroundColor: darkMode ? '#2d2d2d' : 'white',
                    color: darkMode ? '#e0e0e0' : 'black',
                },
            }}
        >
            <DialogTitle sx={{ color: darkMode ? '#e0e0e0' : 'black' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Create Text File
                    <Tooltip title="Help Guide">
                        <IconButton size="small" onClick={() => setHelpOpen(true)}>
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>

            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                centered
                sx={{
                    '& .MuiTab-root': {
                        color: darkMode ? '#e0e0e0' : 'inherit',
                    },
                    '& .Mui-selected': {
                        color: darkMode ? '#90caf9' : '#1976d2',
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: darkMode ? '#90caf9' : '#1976d2',
                    },
                }}
            >
                <Tab label="Create New" />
                <Tab label="Upload File" />
                {!waiting && <Tab label="Bulk Upload" />}
            </Tabs>

            <DialogContent sx={{ backgroundColor: darkMode ? '#2d2d2d' : 'white', maxHeight: '70vh', overflowY: 'auto' }}>
                {waiting?.folderField}
                {error && activeTab !== 2 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {bulkError && activeTab === 2 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {bulkError}
                    </Alert>
                )}

                {/* TAB 0: Create New */}
                {activeTab === 0 && (
                    <Box>
                        <TextField
                            margin="dense"
                            label="File Name"
                            type="text"
                            fullWidth
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            disabled={isLoading}
                            required
                            helperText="Enter file name with or without .txt extension"
                            sx={{
                                mb: 2,
                                '& .MuiInputLabel-root': {
                                    color: darkMode ? '#e0e0e0' : 'inherit',
                                },
                                '& .MuiOutlinedInput-root': {
                                    color: darkMode ? '#e0e0e0' : 'inherit',
                                    '& fieldset': {
                                        borderColor: darkMode ? '#444' : 'inherit',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: darkMode ? '#666' : 'inherit',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: darkMode ? '#90caf9' : '#1976d2',
                                    },
                                },
                                '& .MuiFormHelperText-root': {
                                    color: darkMode ? '#999' : 'inherit',
                                },
                            }}
                        />

                        <TextField
                            margin="dense"
                            label="Content"
                            type="text"
                            fullWidth
                            multiline
                            rows={6}
                            value={fileContent}
                            onChange={(e) => setFileContent(e.target.value)}
                            disabled={isLoading}
                            sx={{
                                mb: 2,
                                '& .MuiInputLabel-root': {
                                    color: darkMode ? '#e0e0e0' : 'inherit',
                                },
                                '& .MuiOutlinedInput-root': {
                                    color: darkMode ? '#e0e0e0' : 'inherit',
                                    '& fieldset': {
                                        borderColor: darkMode ? '#444' : 'inherit',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: darkMode ? '#666' : 'inherit',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: darkMode ? '#90caf9' : '#1976d2',
                                    },
                                },
                            }}
                        />
                    </Box>
                )}

                {/* TAB 1: Upload File */}
                {activeTab === 1 && (
                    <Box>
                        <TextField
                            margin="dense"
                            label="File Name"
                            type="text"
                            fullWidth
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            disabled={isLoading}
                            required
                            helperText={waiting ? 'This starts as the task\'s title.' : 'File name will be auto-filled from selected file'}
                            sx={{
                                mb: 2,
                                '& .MuiInputLabel-root': {
                                    color: darkMode ? '#e0e0e0' : 'inherit',
                                },
                                '& .MuiOutlinedInput-root': {
                                    color: darkMode ? '#e0e0e0' : 'inherit',
                                    '& fieldset': {
                                        borderColor: darkMode ? '#444' : 'inherit',
                                    },
                                },
                            }}
                        />

                        <Box
                            onDrop={handleSingleDrop}
                            onDragOver={(e) => { e.preventDefault(); setIsSingleDragOver(true); }}
                            onDragEnter={(e) => { e.preventDefault(); setIsSingleDragOver(true); }}
                            onDragLeave={() => setIsSingleDragOver(false)}
                            sx={{
                                border: `2px dashed ${isSingleDragOver ? (darkMode ? '#90caf9' : '#1976d2') : (darkMode ? '#666' : '#ccc')}`,
                                borderRadius: '8px',
                                p: 3,
                                textAlign: 'center',
                                mb: 2,
                                cursor: 'pointer',
                                backgroundColor: isSingleDragOver ? (darkMode ? '#3a3a3a' : '#fafafa') : (darkMode ? '#333' : '#f5f5f5'),
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: darkMode ? '#90caf9' : '#1976d2',
                                    backgroundColor: darkMode ? '#3a3a3a' : '#fafafa',
                                },
                            }}
                        >
                            <input
                                type="file"
                                accept=".txt,.text,.html,.htm"
                                onChange={handleFileUpload}
                                disabled={isLoading}
                                style={{ display: 'none' }}
                                id="single-file-input"
                            />
                            <label
                                htmlFor="single-file-input"
                                style={{ cursor: 'pointer', width: '100%', display: 'block' }}
                            >
                                <Typography sx={{ mb: 1 }}>
                                    Click to select or drag and drop your text file here
                                </Typography>
                                <Typography variant="caption" sx={{ color: darkMode ? '#999' : '#666' }}>
                                    Supported formats: .txt, .text, .html, .htm
                                </Typography>
                            </label>
                        </Box>

                        {uploadedFile && (
                            <Box sx={{ mb: 2, p: 2, backgroundColor: darkMode ? '#333' : '#f5f5f5', borderRadius: '8px' }}>
                                <Typography variant="subtitle2">Selected file:</Typography>
                                <Typography sx={{ color: darkMode ? '#90caf9' : '#1976d2' }}>
                                    {uploadedFile.name}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* TAB 2: Bulk Upload */}
                {activeTab === 2 && (
                    <Box>
                        <Box
                            onDrop={handleBulkDrop}
                            onDragOver={(e) => { e.preventDefault(); setIsBulkDragOver(true); }}
                            onDragEnter={(e) => { e.preventDefault(); setIsBulkDragOver(true); }}
                            onDragLeave={() => setIsBulkDragOver(false)}
                            sx={{
                                border: `2px dashed ${isBulkDragOver ? (darkMode ? '#90caf9' : '#1976d2') : (darkMode ? '#666' : '#ccc')}`,
                                borderRadius: '8px',
                                p: 3,
                                textAlign: 'center',
                                mb: 2,
                                cursor: 'pointer',
                                backgroundColor: isBulkDragOver ? (darkMode ? '#3a3a3a' : '#fafafa') : (darkMode ? '#333' : '#f5f5f5'),
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: darkMode ? '#90caf9' : '#1976d2',
                                    backgroundColor: darkMode ? '#3a3a3a' : '#fafafa',
                                },
                            }}
                        >
                            <input
                                type="file"
                                accept=".txt,.text,.html,.htm"
                                multiple
                                onChange={handleBulkFileUpload}
                                disabled={bulkLoading}
                                style={{ display: 'none' }}
                                id="bulk-file-input"
                            />
                            <label
                                htmlFor="bulk-file-input"
                                style={{ cursor: 'pointer', width: '100%', display: 'block' }}
                            >
                                <Typography sx={{ mb: 1 }}>
                                    Click to select or drag and drop multiple text files here
                                </Typography>
                                <Typography variant="caption" sx={{ color: darkMode ? '#999' : '#666' }}>
                                    Select multiple .txt, .text, .html, or .htm files to upload at once
                                </Typography>
                            </label>
                        </Box>

                        {bulkFiles.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Selected Files ({bulkFiles.length}):
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {bulkFiles.map((file, index) => (
                                        <Chip
                                            key={index}
                                            label={file.name}
                                            onDelete={() => handleRemoveBulkFile(index)}
                                            disabled={bulkLoading}
                                            sx={{
                                                backgroundColor: darkMode ? '#404040' : '#e0e0e0',
                                                color: darkMode ? '#e0e0e0' : 'inherit',
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {uploadResults.length > 0 && (
                            <TableContainer component={Paper} sx={{ mb: 2, backgroundColor: darkMode ? '#333' : '#fafafa' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: darkMode ? '#404040' : '#e0e0e0' }}>
                                            <TableCell sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}>File Name</TableCell>
                                            <TableCell sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}>Status</TableCell>
                                            <TableCell sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}>Message</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {uploadResults.map((result, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}>
                                                    {result.fileName}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={result.status}
                                                        size="small"
                                                        color={getStatusColor(result.status)}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}>
                                                    {result.message}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}

                {/* Auto-translate option - shown on all tabs except bulk results */}
                {!(activeTab === 2 && uploadResults.length > 0) && (
                    <Box id="create-auto-translate" sx={{ mt: 1, mb: 0, display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={autoTranslate}
                                    onChange={(e) => setAutoTranslate(e.target.checked)}
                                    disabled={isLoading || bulkLoading}
                                    color="primary"
                                    sx={{ color: darkMode ? '#90caf9' : undefined }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}>
                                    Start AI translation after upload
                                </Typography>
                            }
                        />
                    </Box>
                )}

                {/* Language Selection - Common for all tabs except when showing results */}
                {!(activeTab === 2 && uploadResults.length > 0) && (
                    <Box sx={{ mt: 2 }}>
                        {/* Source Language */}
                        <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                            <InputLabel
                                shrink
                                sx={{
                                    position: 'relative',
                                    transform: 'none',
                                    fontSize: '0.75rem',
                                    mb: 0.5,
                                    color: darkMode ? '#e0e0e0' : 'rgba(0, 0, 0, 0.6)',
                                }}
                            >
                                Source Language
                            </InputLabel>
                            <Select
                                id="source-language"
                                value={sourceLanguage}
                                onChange={handleSourceLanguageChange}
                                disabled={isLoading || bulkLoading}
                                notched={false}
                                sx={{
                                    color: darkMode ? '#e0e0e0' : 'inherit',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? '#444' : 'inherit',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? '#666' : 'inherit',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? '#90caf9' : '#1976d2',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: darkMode ? '#e0e0e0' : 'inherit',
                                    },
                                }}
                            >
                                {sourceLanguageOptions.map((lang) => (
                                    <MenuItem key={lang} value={lang}>
                                        {lang}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Target Languages */}
                        <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                            <InputLabel
                                shrink
                                sx={{
                                    position: 'relative',
                                    transform: 'none',
                                    fontSize: '0.75rem',
                                    mb: 0.5,
                                    color: darkMode ? '#e0e0e0' : 'rgba(0, 0, 0, 0.6)',
                                }}
                            >
                                Target Languages
                            </InputLabel>
                            <SelectLanguageComponent
                                id="create-target-languages"
                                onChange={handleTargetLanguagesChange}
                                languageOptions={targetLanguages.map(l => l.label)}
                                disabled={isLoading || bulkLoading}
                                themeVariant={themeVariant}
                                darkMode={darkMode}
                            />
                        </FormControl>
                    </Box>
                )}

                {bulkLoading && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Uploading {Object.keys(uploadProgress).length} of {bulkFiles.length} files...
                        </Typography>
                        {Object.entries(uploadProgress).map(([index, progress]) => (
                            <Box key={index} sx={{ mb: 1 }}>
                                <Typography variant="caption">
                                    {bulkFiles[parseInt(index)].name}
                                </Typography>
                                <LinearProgress variant="determinate" value={progress || 0} />
                            </Box>
                        ))}
                    </Box>
                )}
                {!waiting && (
                    <TaskFieldsSection
                        value={taskFields}
                        onChange={setTaskFields}
                        contentType="TEXT"
                        defaultLanguages={targetLanguages.map((l) => l.label)}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ backgroundColor: darkMode ? '#2d2d2d' : 'white' }}>
                <Button onClick={handleClose} disabled={isLoading || bulkLoading}>
                    Cancel
                </Button>
                {activeTab === 2 && uploadResults.length > 0 ? (
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        color="primary"
                        disabled={bulkLoading}
                    >
                        Close
                    </Button>
                ) : (
                    <Button
                        onClick={activeTab === 2 ? handleBulkCreate : handleCreate}
                        variant="contained"
                        color="primary"
                        disabled={isLoading || bulkLoading}
                    >
                        {isLoading || bulkLoading ? <CircularProgress size={24} /> : waiting ? 'Create and link' : 'Create'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
        <HelpOverlay
            helpItems={createTextFileDialogHelpItems}
            title="New Text File Guide"
            showButton={false}
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
        />
        </>
    );
};

export default CreateTextFileDialog;
